import React, { useState } from 'react';

import { FsIconButton, SortableTable } from '~/components/core';
import Chip from '~/components/core/Atomic/Chip/Chip';
import { ThreeDotsHorizontalIcon } from '~/components/icons';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';
import type { UserModel } from '~/components/types/user-types';
import { serverDateTimeToLocal } from '~/DateTimeUtils';

import ApproveChangeRequestDialog from './ApproveChangeRequestDialog';
import DeployChangeRequestDialog from './DeployChangeRequestDialog';
import type { ChangeRequest } from './types';

interface ChangeRequestsTableProps {
  rows: ChangeRequest[];
  users: UserModel[];
  organization: Record<string, unknown>;
  onChangeRequestUpdate: () => void;
}

const ChangeRequestsTable: React.FC<ChangeRequestsTableProps> = ({
  rows,
  users,
  organization,
  onChangeRequestUpdate,
}) => {
  const [approveDialogOpen, setApproveDialogOpen] = useState<ChangeRequest | null>(null);
  const [deployDialogOpen, setDeployDialogOpen] = useState<ChangeRequest | null>(null);

  const getUserFullName = (userId: string) => {
    const user = users?.find((user) => user.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : userId;
  };

  const getStatusChip = (status: ChangeRequest['status']) => {
    const statusConfig = {
      pending: { label: 'PENDING', className: 'bg-yellow-100 text-yellow-700' },
      approved: { label: 'APPROVED', className: 'bg-blue-100 text-blue-700' },
      deployed: { label: 'DEPLOYED', className: 'bg-green-100 text-green-700' },
      cancelled: { label: 'CANCELLED', className: 'bg-slate-100 text-slate-700' },
      rejected: { label: 'REJECTED', className: 'bg-red-100 text-red-700' },
    } as const;

    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`rounded-md p-4 font-bold ${config.className}`}>{config.label}</span>;
  };

  const columns = [
    {
      id: 'created_at',
      label: 'Created At',
      specialCell: (row: ChangeRequest) => {
        return row.created_at ? serverDateTimeToLocal(row.created_at) : '';
      },
    },
    {
      id: 'status',
      label: 'Status',
      specialCell: (row: ChangeRequest) => getStatusChip(row.status),
    },
    {
      id: 'requesting_user_id',
      label: 'Requested By',
      specialCell: (row: ChangeRequest) => {
        return <Chip label={getUserFullName(row.requesting_user_id)} size="small" />;
      },
    },
    {
      id: 'changeset_description',
      label: 'Description',
      specialCell: (row: ChangeRequest) => {
        return row.changeset_description ? (
          <OverflowTextWithToolTip maxWidth="200px">{row.changeset_description}</OverflowTextWithToolTip>
        ) : (
          '-'
        );
      },
    },
    {
      id: 'changes_count',
      label: 'Changes Count',
      specialCell: (row: ChangeRequest) => {
        return <span className="rounded-md bg-blue-100 p-4 text-blue-700">{row.changes_count}</span>;
      },
    },
    {
      id: 'approved_at',
      label: 'Approved At',
      specialCell: (row: ChangeRequest) => {
        return row.approved_at ? serverDateTimeToLocal(row.approved_at) : '-';
      },
    },
    {
      id: 'approving_user_id',
      label: 'Approved By',
      specialCell: (row: ChangeRequest) => {
        return row.approving_user_id ? <Chip label={getUserFullName(row.approving_user_id)} size="small" /> : '-';
      },
    },
    {
      id: 'changeset_uuid',
      label: 'UUID',
      width: '120px',
      specialCell: (row: ChangeRequest) => {
        return <OverflowTextWithToolTip maxWidth="120px">{row.changeset_uuid}</OverflowTextWithToolTip>;
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      width: '140px',
      specialCell: (row: ChangeRequest) => {
        return (
          <div className="flex space-x-8">
            {row.status === 'pending' && (
              <FsIconButton
                icon={ThreeDotsHorizontalIcon}
                tooltipText="Approve change request"
                className="cursor-pointer hover:stroke-teal-700"
                onClick={() => setApproveDialogOpen(row)}
              />
            )}
            {row.status === 'approved' && (
              <FsIconButton
                icon={ThreeDotsHorizontalIcon}
                tooltipText="Deploy change request"
                className="cursor-pointer hover:stroke-green-700"
                onClick={() => setDeployDialogOpen(row)}
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <SortableTable columns={columns} rows={rows} order="desc" defaultOrderColumn={0} />
      {/* Approval Dialog */}
      {approveDialogOpen && (
        <ApproveChangeRequestDialog
          onClose={() => setApproveDialogOpen(null)}
          organization={organization}
          changeRequest={approveDialogOpen}
          users={users}
          onSuccess={() => {
            setApproveDialogOpen(null);
            onChangeRequestUpdate();
          }}
        />
      )}

      {/* Deploy Dialog */}
      {deployDialogOpen && (
        <DeployChangeRequestDialog
          onClose={() => setDeployDialogOpen(null)}
          organization={organization}
          changeRequest={deployDialogOpen}
          users={users}
          onSuccess={() => {
            setDeployDialogOpen(null);
            onChangeRequestUpdate();
          }}
        />
      )}
    </>
  );
};

export default ChangeRequestsTable;
